import * as i1 from '@angular/common';
import { CommonModule, NgClass, NgTemplateOutlet } from '@angular/common';
import * as i0 from '@angular/core';
import { HostListener, HostBinding, Input, ViewEncapsulation, Component, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { RouterLink, RouterModule } from '@angular/router';
import { CentricIcon2Component, CentricIcon2Module } from '@windmill/ng-windmill/icon';
import { ActionBaseDirective } from '@windmill/ng-windmill/shared';

/**
 * To use this component in a module, you can import the `CentricLinkModule`:
 * @example
 * ```typescript
 * import { CentricLinkModule } from '@windmill/ng-windmill/link';
 * ```
 *
 * To use the standalone component directly, you can import `LinkComponent`:
 * @example
 * ```typescript
 * import { LinkComponent } from '@windmill/ng-windmill/link';
 * ```
 */
class LinkComponent extends ActionBaseDirective {
    /**
     * Link data-testid
     */
    testLinkId;
    /**
     * Href for link
     */
    innerHref;
    /**
     * Router link for link (navigate without reload)
     */
    innerRouterLink;
    /**
     * Query params for router link
     */
    queryParams;
    /**
     * Fragment for router link
     */
    fragment;
    /**
     * Target for link
     */
    target = '_self';
    /**
     * Rel for link
     */
    rel;
    /**
     * Title for link
     */
    title;
    /**
     * The attribute specifies that the target (the file specified in the href attribute) will be downloaded when a user clicks on the hyperlink.
     */
    download;
    /**
     * The attribute specifies which referrer information to use when fetching the resource.
     */
    referrerpolicy = 'no-referrer-when-downgrade';
    /**
     * If true, the anchor element will have aria-disabled
     */
    disabled = false;
    onClick(event) {
        if (this.disabled) {
            event.preventDefault();
        }
    }
    getInnerHref() {
        return this.disabled ? null : this.innerHref;
    }
    getInnerRouterLink() {
        return this.disabled ? null : this.innerRouterLink;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: LinkComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: LinkComponent, isStandalone: true, selector: "centric-link", inputs: { testLinkId: "testLinkId", innerHref: "innerHref", innerRouterLink: "innerRouterLink", queryParams: "queryParams", fragment: "fragment", target: "target", rel: "rel", title: "title", download: "download", referrerpolicy: "referrerpolicy", disabled: "disabled" }, host: { listeners: { "click": "onClick($event)" }, properties: { "class.is-disabled": "this.disabled" } }, usesInheritance: true, ngImport: i0, template: "@if (!innerRouterLink) {\r\n  <a\r\n    class=\"centric-inner-button\"\r\n    [ngClass]=\"buttonClass\"\r\n    [class.has-icon]=\"iconId\"\r\n    [attr.href]=\"getInnerHref()\"\r\n    [attr.download]=\"download ? '' : null\"\r\n    [attr.referrerpolicy]=\"referrerpolicy\"\r\n    [attr.target]=\"target\"\r\n    [attr.rel]=\"rel\"\r\n    [attr.title]=\"title\"\r\n    [attr.aria-disabled]=\"disabled\"\r\n    [attr.aria-label]=\"ariaLabel\"\r\n    [attr.aria-labelledby]=\"ariaLabelledby\"\r\n    [attr.aria-description]=\"ariaDescription\"\r\n    [attr.aria-describedby]=\"ariaDescribedby\"\r\n    [attr.data-testid]=\"testLinkId\"\r\n    [attr.tabindex]=\"disabled ? '-1' : tabIndex\">\r\n    <ng-container *ngTemplateOutlet=\"linkContent\"></ng-container>\r\n  </a>\r\n} @else {\r\n  <a\r\n    class=\"centric-inner-button\"\r\n    [ngClass]=\"buttonClass\"\r\n    [class.has-icon]=\"iconId\"\r\n    [routerLink]=\"getInnerRouterLink()\"\r\n    [queryParams]=\"queryParams\"\r\n    [fragment]=\"fragment\"\r\n    [attr.title]=\"title\"\r\n    [attr.aria-disabled]=\"disabled\"\r\n    [attr.aria-label]=\"ariaLabel\"\r\n    [attr.aria-labelledby]=\"ariaLabelledby\"\r\n    [attr.aria-description]=\"ariaDescription\"\r\n    [attr.aria-describedby]=\"ariaDescribedby\"\r\n    [attr.data-testid]=\"testLinkId\"\r\n    [attr.tabindex]=\"disabled ? '-1' : tabIndex\">\r\n    <ng-container *ngTemplateOutlet=\"linkContent\"></ng-container>\r\n  </a>\r\n}\r\n\r\n<ng-template #linkContent>\r\n  @if (iconId) {\r\n    <centric-icon2\r\n      [svgIcon]=\"iconId\"\r\n      [size]=\"determineIconSize\"\r\n      (click)=\"onClick($event)\">\r\n    </centric-icon2>\r\n  }\r\n  <span class=\"button-content\">\r\n    <ng-content></ng-content>\r\n  </span>\r\n</ng-template>\r\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "ngmodule", type: RouterModule }, { kind: "component", type: CentricIcon2Component, selector: "centric-icon2", inputs: ["size", "inline", "svgIcon"] }], encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: LinkComponent, decorators: [{
            type: Component,
            args: [{ selector: 'centric-link', encapsulation: ViewEncapsulation.None, imports: [CommonModule, NgClass, NgTemplateOutlet, RouterLink, RouterModule, CentricIcon2Component], standalone: true, template: "@if (!innerRouterLink) {\r\n  <a\r\n    class=\"centric-inner-button\"\r\n    [ngClass]=\"buttonClass\"\r\n    [class.has-icon]=\"iconId\"\r\n    [attr.href]=\"getInnerHref()\"\r\n    [attr.download]=\"download ? '' : null\"\r\n    [attr.referrerpolicy]=\"referrerpolicy\"\r\n    [attr.target]=\"target\"\r\n    [attr.rel]=\"rel\"\r\n    [attr.title]=\"title\"\r\n    [attr.aria-disabled]=\"disabled\"\r\n    [attr.aria-label]=\"ariaLabel\"\r\n    [attr.aria-labelledby]=\"ariaLabelledby\"\r\n    [attr.aria-description]=\"ariaDescription\"\r\n    [attr.aria-describedby]=\"ariaDescribedby\"\r\n    [attr.data-testid]=\"testLinkId\"\r\n    [attr.tabindex]=\"disabled ? '-1' : tabIndex\">\r\n    <ng-container *ngTemplateOutlet=\"linkContent\"></ng-container>\r\n  </a>\r\n} @else {\r\n  <a\r\n    class=\"centric-inner-button\"\r\n    [ngClass]=\"buttonClass\"\r\n    [class.has-icon]=\"iconId\"\r\n    [routerLink]=\"getInnerRouterLink()\"\r\n    [queryParams]=\"queryParams\"\r\n    [fragment]=\"fragment\"\r\n    [attr.title]=\"title\"\r\n    [attr.aria-disabled]=\"disabled\"\r\n    [attr.aria-label]=\"ariaLabel\"\r\n    [attr.aria-labelledby]=\"ariaLabelledby\"\r\n    [attr.aria-description]=\"ariaDescription\"\r\n    [attr.aria-describedby]=\"ariaDescribedby\"\r\n    [attr.data-testid]=\"testLinkId\"\r\n    [attr.tabindex]=\"disabled ? '-1' : tabIndex\">\r\n    <ng-container *ngTemplateOutlet=\"linkContent\"></ng-container>\r\n  </a>\r\n}\r\n\r\n<ng-template #linkContent>\r\n  @if (iconId) {\r\n    <centric-icon2\r\n      [svgIcon]=\"iconId\"\r\n      [size]=\"determineIconSize\"\r\n      (click)=\"onClick($event)\">\r\n    </centric-icon2>\r\n  }\r\n  <span class=\"button-content\">\r\n    <ng-content></ng-content>\r\n  </span>\r\n</ng-template>\r\n" }]
        }], propDecorators: { testLinkId: [{
                type: Input
            }], innerHref: [{
                type: Input
            }], innerRouterLink: [{
                type: Input
            }], queryParams: [{
                type: Input
            }], fragment: [{
                type: Input
            }], target: [{
                type: Input
            }], rel: [{
                type: Input
            }], title: [{
                type: Input
            }], download: [{
                type: Input
            }], referrerpolicy: [{
                type: Input
            }], disabled: [{
                type: HostBinding,
                args: ['class.is-disabled']
            }, {
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

const moduleComponents = [LinkComponent];
class CentricLinkModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricLinkModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: CentricLinkModule, imports: [CommonModule, RouterModule, CentricIcon2Module, LinkComponent], exports: [LinkComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricLinkModule, imports: [CommonModule, RouterModule, CentricIcon2Module, moduleComponents] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricLinkModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, RouterModule, CentricIcon2Module, ...moduleComponents],
                    exports: [...moduleComponents],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CentricLinkModule, LinkComponent };
